// graphAvailableRefs — what the CURRENT character actually has, for the paste
// report's "does this reference resolve?" diff.
//
// nodeClipboard.resolveRequires takes this as an argument rather than reaching
// for it, so the rules stay pure and node-testable while the reaching happens
// here. This module is the thin adapter over the live registries; it is not
// unit-tested for the same reason PlayClipNode's dropdown is not — it only
// forwards what CustomAssetManager already owns.
//
// Clip sources MIRROR PlayClipNode's dropdown deliberately: the paste report
// must say "not on this character" for exactly the clips the node's own picker
// would not offer. Any divergence would report a clip as missing that the user
// can plainly see in the dropdown, which teaches them to distrust the report.

import { CustomAssetManager } from './CustomAssetManager.js'
import { variableDefs } from './animVariables.js'

/**
 * Clip names an anim graph of `graphType` can actually play: the character's
 * animation assets of that kind, plus gestures and prefabs, which register
 * under their own names (face variants carry the `_face` suffix).
 */
function clipNamesFor(graphType) {
  const kind = graphType === 'face' ? 'face' : 'body'
  const names = CustomAssetManager.listAnimationAssets()
    .filter(a => a.kind === kind)
    .map(a => a.name)

  const gestures = CustomAssetManager.listGestures()
  const prefabs = CustomAssetManager.listPrefabs()
  if (kind === 'body') {
    names.push(...gestures.filter(g => g.body_glb_url).map(g => g.name))
    names.push(...prefabs.filter(p => p.body_glb_url).map(p => p.name))
  } else {
    names.push(...gestures.filter(g => g.face_json_url).map(g => `${g.name}_face`))
    names.push(...prefabs.filter(p => p.face_json_url).map(p => `${p.name}_face`))
  }
  return names
}

/**
 * The event graph can play BOTH families (playBodyClip / playFaceClip), so its
 * available set is the union — a body clip referenced from an event node is
 * present, and reporting it as missing would be wrong.
 */
function allClipNames() {
  return [...clipNamesFor('body'), ...clipNamesFor('face')]
}

/**
 * @param {'body'|'face'|'events'} kind  the graph being pasted INTO.
 * @param {object} extra
 *   variables — override the declared variable list (the event editor owns its
 *     own working copy; the anim editors read the shared animVariables defs).
 *   functions — the events document's functions map.
 */
export function availableRefsFor(kind, { variables, functions } = {}) {
  const varNames = (variables ?? variableDefs ?? []).map(v => v?.name).filter(Boolean)
  return {
    clips: kind === 'events' ? allClipNames() : clipNamesFor(kind),
    audioAssetIds: CustomAssetManager.listAudioAssets().map(a => a.id).filter(Boolean),
    variables: varNames,
    functions: Object.keys(functions ?? {}),
  }
}
